import { InputLocked, PlayerTag, Transform } from '@/components'
import type { SystemContext } from '@/core/ecs/Scheduler'
import type { World } from '@/core/ecs/World'
import { ArenaResource, type ArenaState, type SpawnPoint } from '@/game/arenas/Arena'
import { arenaById, scaledArena } from '@/game/arenas'
import { Events } from '@/game/events'
import { spawnHazard, spawnPlayer, spawnProp } from '@/game/prefabs'
import { EventsResource, MatchResource, type MatchState } from '@/game/resources'
import { SceneBridgeResource } from '@/gfx/SceneBridge'

/**
 * The first countdown is longer because players are still reading the arena;
 * every one after that is short, because they are not. Between them the old
 * 3.2 + 2.4 s of locked input was more dead air than some rounds had gameplay.
 */
const COUNTDOWN_FIRST = 3.2
const COUNTDOWN_SECONDS = 2.2
/** How long a double knock-out stays possible after the first fall. */
const RESOLVE_WINDOW = 0.25
const ROUND_OVER_SECONDS = 1.5

/**
 * Drives the match state machine: countdown → fight → resolve → round over →
 * next round or match over.
 *
 * The short `resolving` window exists so that trading a knock-out — both
 * fighters flying off together — reads as a draw instead of handing the point
 * to whoever's collider happened to cross the edge one step later.
 */
export function roundSystem({ world, dt }: SystemContext): void {
  const match = world.resource(MatchResource)
  const events = world.resource(EventsResource)

  // Falls are recorded in every live phase; the phase decides what to do next.
  for (const fall of events.read(Events.Fall)) {
    if (fall.player === null) continue
    match.fallen[fall.player] = true
  }

  switch (match.phase) {
    case 'countdown': {
      match.phaseTimer -= dt
      if (match.phaseTimer <= 0) {
        match.phase = 'fight'
        match.roundTime = 0
        setInputLock(world, false)
        events.emit(Events.Round, { phase: 'fight', winner: null })
      }
      break
    }

    case 'fight': {
      match.roundTime += dt
      // The round ends when one team is left, which covers a free-for-all
      // without a second code path: there, every fighter is their own team.
      if (teamsStanding(match).length <= 1) {
        match.phase = 'resolving'
        match.phaseTimer = RESOLVE_WINDOW
      } else if (match.roundTime >= match.roundLimit) {
        // The collapsing platform makes this all but unreachable; it is here so
        // a stalemate can never hang the match.
        concludeRound(world, match, null, true)
      }
      break
    }

    case 'resolving': {
      match.phaseTimer -= dt
      if (match.phaseTimer > 0) break
      // The window exists so a trade — everyone flying off together — reads as
      // a draw rather than crowning whoever's collider crossed a step later.
      const alive = teamsStanding(match)
      const winner = alive.length === 1 ? alive[0] : null
      concludeRound(world, match, winner, winner === null)
      break
    }

    case 'roundOver': {
      match.phaseTimer -= dt
      if (match.phaseTimer > 0) break
      if (match.matchWinner !== null) {
        match.phase = 'matchOver'
        events.emit(Events.Round, { phase: 'matchOver', winner: match.matchWinner })
      } else {
        startRound(world)
      }
      break
    }

    default:
      break
  }
}

/** Teams with at least one fighter still on the platform. */
function teamsStanding(match: MatchState): number[] {
  const alive: number[] = []
  for (let i = 0; i < match.controllers.length; i++) {
    if (match.fallen[i]) continue
    const team = match.teams[i] ?? i
    if (!alive.includes(team)) alive.push(team)
  }
  return alive
}

/**
 * `winner` is a **team**, not a slot. In a free-for-all the two are the same
 * number, which is why nothing else in the game had to learn about teams.
 */
function concludeRound(
  world: World,
  match: MatchState,
  winner: number | null,
  draw: boolean,
): void {
  const events = world.resource(EventsResource)
  match.phase = 'roundOver'
  match.phaseTimer = ROUND_OVER_SECONDS
  match.roundWinner = winner
  match.lastRoundDraw = draw

  if (winner !== null) {
    match.score[winner]++
    if (match.score[winner] >= match.winsNeeded) match.matchWinner = winner
  }

  setInputLock(world, true)
  events.emit(Events.Round, { phase: 'over', winner })
}

function setInputLock(world: World, locked: boolean): void {
  for (const e of world.query([PlayerTag]).entities()) {
    if (locked) world.addTag(e, InputLocked)
    else world.remove(e, InputLocked)
  }
}

/** Tears the arena down and builds a fresh round on it. */
export function startRound(world: World): void {
  const match = world.resource(MatchResource)
  const arena = world.resource(ArenaResource)
  const events = world.resource(EventsResource)
  const def = scaledArena(arenaById(match.arenaId), match.arenaScale)

  clearRoundEntities(world)
  arena.reset(def)
  // The simulation's arena is restored above; the visuals hold their own state
  // (which slabs fell) and must be restored too, or the next round is fought on
  // a platform that looks broken but behaves whole.
  world.tryResource(SceneBridgeResource)?.resetArena()

  match.phase = 'countdown'
  match.phaseTimer = match.roundIndex === 0 ? COUNTDOWN_FIRST : COUNTDOWN_SECONDS
  match.roundTime = 0
  match.roundWinner = null
  match.fallen = match.controllers.map(() => false)
  match.roundIndex++

  for (const spec of def.props) spawnProp(world, spec)
  for (const spec of def.hazards) spawnHazard(world, spec)

  const spawns = spawnRing(arena, match.controllers.length)
  match.players = match.controllers.map((controller, slot) =>
    spawnPlayer(world, slot, spawns[slot], {
      model: match.models[slot] ?? 'thin',
      ai: controller === 'ai' ? match.difficulty : null,
    }),
  )
  setInputLock(world, true)

  events.emit(Events.Round, { phase: 'countdown', winner: null })
}

/** Starts a brand new match on the given arena. */
export function startMatch(world: World, arenaId: string, winsNeeded: number): void {
  const match = world.resource(MatchResource)
  match.arenaId = arenaId
  match.winsNeeded = winsNeeded
  match.score = match.controllers.map(() => 0)
  match.roundIndex = 0
  match.matchWinner = null
  match.lastRoundDraw = false
  startRound(world)
}

/**
 * Evenly spaced starting positions facing the middle.
 *
 * A duel uses the arena's two hand-placed spawns, which are tuned per map (the
 * ring arena's, for instance, are deliberately not antipodal). Anything larger
 * is generated: hand-authoring six positions for six arenas is a lot of numbers
 * to keep fair, and a ring pulled inside the footprint is fair by construction.
 */
function spawnRing(arena: ArenaState, count: number): SpawnPoint[] {
  if (count === 2) return [arena.def.spawns[0], arena.def.spawns[1]]

  const radius = Math.min(arena.def.extentX, arena.def.extentZ) * 0.66
  const points: SpawnPoint[] = []
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2 + Math.PI / count
    const raw = {
      x: arena.def.centerX + Math.sin(a) * radius,
      z: arena.def.centerZ + Math.cos(a) * radius,
    }
    // Pull inside the footprint: on the cross and the bridge a plain ring puts
    // half the fighters in the void.
    const safe = arena.clampInside(raw.x, raw.z, 2)
    points.push({ x: safe.x, z: safe.z, angle: Math.atan2(-safe.x, -safe.z) })
  }
  return points
}

/** Removes everything a round created — used when returning to the menu. */
export function clearRound(world: World): void {
  clearRoundEntities(world)
}

function clearRoundEntities(world: World): void {
  // Everything in a round carries a Transform: players, props, hazards, fields.
  for (const e of [...world.store(Transform).owners]) world.destroyEntity(e)
  world.flushDestroyed()
}
